import React from 'react';
import LegalLayout, { L } from './LegalLayout';
import { OPERATOR } from '../../legal/operator';

export default function PrivacyPolicyPage() {
  return (
    <LegalLayout title="Privacy Policy">
      <L.P>
        This Privacy Policy explains how {OPERATOR.productName} (“we”, “us”) collects, uses, stores and
        shares personal information when you use {OPERATOR.productName} (the “Service”), available at{' '}
        <L.A href={OPERATOR.productUrl}>{OPERATOR.productUrl}</L.A>.
      </L.P>
      <L.P>
        {OPERATOR.productName} is operated by an individual based in {OPERATOR.country}. We process
        personal data as a controller under the EU General Data Protection Regulation (GDPR) and
        equivalent privacy laws. For any privacy-related question, write to{' '}
        <L.Mail to={OPERATOR.supportEmail} />.
      </L.P>

      <L.H2>1. What we collect</L.H2>
      <L.P>The data we hold about you falls into the following categories:</L.P>
      <L.UL>
        <L.LI><strong>Account data</strong> — your email address, a securely hashed version of your password (we never store passwords in readable form), account creation date, account role and subscription tier.</L.LI>
        <L.LI><strong>Portfolio data</strong> — portfolios you create, the instruments you track and the transactions you record, together with any notes you add.</L.LI>
        <L.LI><strong>Technical data</strong> — transient information needed to serve your requests, plus security and diagnostic logs generated by our hosting providers.</L.LI>
        <L.LI><strong>Local browser storage</strong> — items kept in your browser to keep you signed in and remember your preferences. They stay on your device; only authentication tokens leave the browser, and only to authorize your requests.</L.LI>
      </L.UL>
      <L.P>We do not collect special-category data (health, ethnicity, biometrics, etc.) and we do not knowingly process data of children under 16.</L.P>

      <L.H2>2. Why we process it (legal bases)</L.H2>
      <L.UL>
        <L.LI><strong>Contract (Art. 6(1)(b) GDPR)</strong> — providing the Service: creating your account, storing your portfolios, computing performance, sending transactional emails (registration, password reset).</L.LI>
        <L.LI><strong>Legitimate interest (Art. 6(1)(f))</strong> — keeping the Service secure and stable: server logs, abuse prevention, debugging.</L.LI>
        <L.LI><strong>Consent (Art. 6(1)(a))</strong> — any optional cookies or analytics we may introduce in the future will only run after you opt in.</L.LI>
      </L.UL>

      <L.H2>3. How long we keep it</L.H2>
      <L.UL>
        <L.LI>Account and portfolio data — as long as your account exists, plus up to 30 days after deletion to allow recovery and audit.</L.LI>
        <L.LI>Server logs — typically up to 30 days, then automatically rotated and deleted by our hosting providers.</L.LI>
        <L.LI>Backups — encrypted snapshots may be retained for up to 30 days for disaster recovery.</L.LI>
      </L.UL>

      <L.H2>4. Who we share it with</L.H2>
      <L.P>
        We never sell your data. We share limited data with the third-party service providers
        (“subprocessors”) that help us run {OPERATOR.productName}. The current list is published at{' '}
        <L.A href="/subprocessors">/subprocessors</L.A>.
      </L.P>
      <L.P>
        Some subprocessors are located outside the European Economic Area (notably the United States).
        Where they are, we rely on the EU Standard Contractual Clauses or equivalent safeguards.
      </L.P>

      <L.H2>5. Your rights</L.H2>
      <L.P>Under GDPR (and equivalent laws elsewhere) you have the right to:</L.P>
      <L.UL>
        <L.LI>Access the personal data we hold about you.</L.LI>
        <L.LI>Receive your data in a portable, machine-readable format (data export).</L.LI>
        <L.LI>Correct inaccurate data.</L.LI>
        <L.LI>Delete your account and the associated data.</L.LI>
        <L.LI>Object to processing or request restriction.</L.LI>
        <L.LI>Lodge a complaint with a supervisory authority (in the Czech Republic: the Office for Personal Data Protection — <L.A href="https://www.uoou.cz/">uoou.cz</L.A>).</L.LI>
      </L.UL>
      <L.P>
        You can delete your account from your profile settings. For other requests, write to{' '}
        <L.Mail to={OPERATOR.supportEmail} />.
      </L.P>

      <L.H2>6. Security</L.H2>
      <L.P>
        We protect your data with industry-standard measures: encrypted transport (TLS),
        salted-hash storage of passwords, short-lived authentication tokens, restricted database
        access and regular security updates of our infrastructure. No system is ever 100% secure,
        but we take security seriously and continuously improve our defences.
      </L.P>

      <L.H2>7. Cookies and tracking</L.H2>
      <L.P>
        {OPERATOR.productName} currently uses <strong>only strictly-necessary local storage</strong> —
        no advertising or third-party analytics cookies. See the{' '}
        <L.A href="/cookies">Cookies page</L.A> for details.
      </L.P>

      <L.H2>8. No investment advice</L.H2>
      <L.P>
        {OPERATOR.productName} is a personal portfolio tracker. The data shown is for informational
        purposes only and does not constitute investment advice. Market data is provided “as-is”
        from third parties and may contain errors or delays.
      </L.P>

      <L.H2>9. Changes to this policy</L.H2>
      <L.P>
        We may update this Privacy Policy from time to time. Material changes will be announced by
        email or in-app banner before they take effect. The current version is always available at{' '}
        <L.A href="/privacy">/privacy</L.A>.
      </L.P>

      <L.H2>10. Contact</L.H2>
      <L.P>
        Questions about this policy or your data? Write to <L.Mail to={OPERATOR.supportEmail} />.
      </L.P>
    </LegalLayout>
  );
}
